import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle, Clock, Building2, ClipboardList, CheckCircle } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getAlerts, dismissAlert } from '../services/api';
import { toast } from 'sonner';

export const AlertBell = () => {
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({ total: 0, critical: 0, warning: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await getAlerts();
      setAlerts(res.data.alerts || []);
      setSummary(res.data.summary || { total: 0, critical: 0, warning: 0 });
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 120000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const handleAlertClick = (alert) => {
    setOpen(false);
    if (alert.type === 'task') navigate(`/tasks?highlight=${alert.entity_id}`);
    else navigate(`/referrals?highlight=${alert.entity_id}`);
  };

  const handleDismiss = async (e, alert) => {
    e.stopPropagation();
    try {
      await dismissAlert(alert.id);
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
      setSummary(prev => ({
        ...prev,
        total: prev.total - 1,
        critical: alert.level === 'critical' ? prev.critical - 1 : prev.critical,
        warning: alert.level === 'warning' ? prev.warning - 1 : prev.warning
      }));
      toast.success('Alerta marcada como atendida');
    } catch {
      toast.error('Error descartando alerta');
    }
  };

  const getIcon = (type) => {
    if (type === 'task') return <ClipboardList className="w-4 h-4" />;
    if (type === 'referral_informe') return <Building2 className="w-4 h-4" />;
    return <Clock className="w-4 h-4" />;
  };

  const getLevelStyle = (level) => {
    if (level === 'critical') return 'border-l-red-500 bg-red-50/80';
    return 'border-l-amber-400 bg-amber-50/60';
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="alert-bell-btn"
      >
        <Bell className={`w-5 h-5 ${summary.total > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
        {summary.total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="alert-count-badge">
            {summary.total > 9 ? '9+' : summary.total}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col p-0" data-testid="alert-panel">
          <DialogHeader className="p-4 bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-t-lg">
            <DialogTitle className="flex items-center gap-2 text-white">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Alertas Activas
              {summary.critical > 0 && (
                <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0 ml-2">{summary.critical} criticas</Badge>
              )}
              {summary.warning > 0 && (
                <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0">{summary.warning} aviso</Badge>
              )}
            </DialogTitle>
            <p className="text-xs text-slate-300 sr-only">Panel de alertas del sistema</p>
          </DialogHeader>

          <div className="overflow-y-auto flex-1">
            {alerts.length === 0 ? (
              <div className="p-10 text-center text-slate-400">
                <Bell className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm font-medium">Sin alertas activas</p>
                <p className="text-xs mt-1">Todo esta al dia</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`w-full text-left p-3.5 hover:bg-slate-50 transition-colors border-l-4 flex items-start gap-1 ${getLevelStyle(alert.level)}`}
                    data-testid={`alert-item-${alert.id}`}
                  >
                    <button
                      onClick={() => handleAlertClick(alert)}
                      className="flex items-start gap-3 flex-1 min-w-0 text-left"
                    >
                      <div className={`p-1.5 rounded-md mt-0.5 ${alert.level === 'critical' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                        {getIcon(alert.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{alert.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{alert.message}</p>
                        {alert.clinic && (
                          <p className="text-xs text-slate-400 mt-0.5">{alert.clinic}</p>
                        )}
                      </div>
                      <span className={`text-xs font-bold px-2 py-1 rounded-lg shrink-0 ${alert.level === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {alert.days_pending}d
                      </span>
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 h-8 w-8 text-green-600 hover:text-green-800 hover:bg-green-50"
                      onClick={(e) => handleDismiss(e, alert)}
                      title="Marcar como atendida"
                      data-testid={`dismiss-alert-${alert.id}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {alerts.length > 0 && (
            <div className="p-3 bg-slate-50 border-t text-center">
              <span className="text-xs text-slate-500">
                {summary.tasks > 0 && `${summary.tasks} tareas`}
                {summary.tasks > 0 && (summary.referrals_informe + summary.referrals_seguimiento) > 0 && ' · '}
                {(summary.referrals_informe + summary.referrals_seguimiento) > 0 && `${summary.referrals_informe + summary.referrals_seguimiento} remitidos`}
              </span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
